// PisosScreen.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import BackspaceIcon from "@mui/icons-material/Backspace";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import HistoryIcon from "@mui/icons-material/History";
import PisosMoneyTokens from "../components/PisosMoneyTokens";

const DINERO_INICIAL = 400;
const IMPUESTOS = [50, 0];
const PLAYER_COUNTS = [2, 3, 4, 5, 6, 7, 8];
const LS_KEY = "propiedades_personas";

const DEFAULT_NAMES = [
    "Ale",
    "Pablo",
    "Jorge",
    "Jugador 4",
    "Jugador 5",
    "Jugador 6",
    "Jugador 7",
    "Jugador 8",
];

const endsWithOp = (s) => s.endsWith("+") || s.endsWith("-");

const roundMoney = (n) => Math.round(Number(n) * 100) / 100;

const parseTerm = (num) => {
    const value = Number(num.replace(",", "."));
    return Number.isFinite(value) ? value : 0;
};

const computeValor = (raw) => {
    if (!raw) return 0;

    let s = raw;
    while (s.length && endsWithOp(s)) s = s.slice(0, -1);
    if (!s) return 0;

    let total = 0;
    let num = "";
    let sign = 1;

    for (let i = 0; i < s.length; i++) {
        const ch = s[i];

        if ((ch >= "0" && ch <= "9") || ch === ",") {
            if (ch !== "," || !num.includes(",")) num += ch;
            continue;
        }

        if (ch === "+" || ch === "-") {
            if (num !== "") {
                total += sign * parseTerm(num);
                num = "";
            }
            sign = ch === "+" ? 1 : -1;
        }
    }

    if (num !== "") total += sign * parseTerm(num);

    return roundMoney(total);
};

const formatMoney = (n) => {
    const num = roundMoney(n);
    if (Number.isNaN(num)) return "0";

    const text = Number.isInteger(num)
        ? String(num)
        : num.toFixed(2).replace(/\.?0+$/, "");

    return text.replace(".", ",");
};

const createPersonas = (count, current = []) =>
    Array.from({ length: count }, (_, idx) => {
        const prev = current[idx];

        return {
            id: prev?.id ?? `p${idx + 1}`,
            name: (
                prev?.name ??
                DEFAULT_NAMES[idx] ??
                `Jugador ${idx + 1}`
            ).trim(),
            amount: Number.isFinite(Number(prev?.amount))
                ? Number(prev.amount)
                : DINERO_INICIAL,
        };
    });

const loadPersonasFromStorage = () => {
    // NOTE: keep it safe
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || !parsed.length) return null;

        return parsed.map((p, idx) => ({
            id: p.id ?? `p${idx + 1}`,
            name: (p.name ?? DEFAULT_NAMES[idx] ?? `Jugador ${idx + 1}`).trim(),
            amount: Number.isFinite(Number(p.amount))
                ? Number(p.amount)
                : DINERO_INICIAL,
        }));
    } catch {
        return null;
    }
};

const PisosScreen = () => {
    const [cuenta, setCuenta] = useState("");
    const valor = useMemo(() => computeValor(cuenta), [cuenta]);

    const [personas, setPersonas] = useState(() => {
        const saved = loadPersonasFromStorage();
        return saved ?? createPersonas(3);
    });

    const [compradorId, setCompradorId] = useState(null);
    const [vendedorId, setVendedorId] = useState(null);
    const [lastSelectedTap, setLastSelectedTap] = useState(null);

    const [taxIndex, setTaxIndex] = useState(0);
    const taxRate = IMPUESTOS[taxIndex];

    const [historialOpen, setHistorialOpen] = useState(false);
    const [movimientos, setMovimientos] = useState([]);
    const [resetOpen, setResetOpen] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [editPersonas, setEditPersonas] = useState([]);

    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "warning",
    });

    useEffect(() => {
        // NOTE: persist scores
        try {
            localStorage.setItem(LS_KEY, JSON.stringify(personas));
        } catch {
            // NOTE: ignore storage errors
        }
    }, [personas]);

    const showToast = (message, severity = "warning") => {
        setToast({ open: true, message, severity });
    };

    const closeToast = (_event, reason) => {
        if (reason === "clickaway") return;
        setToast((prev) => ({ ...prev, open: false }));
    };

    const clearSelection = () => {
        setCompradorId(null);
        setVendedorId(null);
        setLastSelectedTap(null);
    };

    const onJugadorClick = (id) => {
        if (!compradorId && !vendedorId) {
            setCompradorId(id);
            setLastSelectedTap(null);
            return;
        }

        if (compradorId && !vendedorId) {
            if (id === compradorId) {
                clearSelection();
                return;
            }

            setVendedorId(id);
            setLastSelectedTap(null);
            return;
        }

        if (compradorId && vendedorId) {
            if (id !== compradorId && id !== vendedorId) {
                setCompradorId(id);
                setVendedorId(null);
                setLastSelectedTap(null);
                return;
            }

            if (lastSelectedTap === id) {
                if (id === compradorId) {
                    setCompradorId(vendedorId);
                    setVendedorId(null);
                } else {
                    setVendedorId(null);
                }

                setLastSelectedTap(null);
                return;
            }

            setCompradorId(vendedorId);
            setVendedorId(compradorId);
            setLastSelectedTap(id);
        }
    };

    const onDigit = (d) => {
        setCuenta(`${cuenta}${d}`);
    };

    const onComma = () => {
        const lastPart = cuenta.split(/[+-]/).pop() ?? "";
        if (lastPart.includes(",")) return;

        setCuenta(`${cuenta}${!cuenta || endsWithOp(cuenta) ? "0," : ","}`);
    };

    const onOp = (op) => {
        if (!cuenta) {
            if (op === "-") setCuenta("-");
            return;
        }

        if (endsWithOp(cuenta)) {
            setCuenta(`${cuenta.slice(0, -1)}${op}`);
            return;
        }

        setCuenta(`${cuenta}${op}`);
    };

    const onBackspace = () => {
        if (!cuenta) return;
        setCuenta(cuenta.slice(0, -1));
    };

    const onAplicar = () => {
        if (!compradorId && !vendedorId) {
            showToast("Debes seleccionar al menos un jugador.", "warning");
            return;
        }

        if (!valor) return;

        if (compradorId && vendedorId) {
            if (valor <= 0) {
                showToast(
                    "El precio de compra debe ser mayor que 0.",
                    "warning",
                );
                return;
            }

            const comprador = personas.find((p) => p.id === compradorId);
            const vendedor = personas.find((p) => p.id === vendedorId);
            if (!comprador || !vendedor) return;

            const precio = roundMoney(valor);
            const ingresoVendedor = roundMoney(
                precio * ((100 - taxRate) / 100),
            );

            const changes = [
                {
                    id: comprador.id,
                    name: comprador.name,
                    before: comprador.amount,
                    after: roundMoney(comprador.amount - precio),
                },
                {
                    id: vendedor.id,
                    name: vendedor.name,
                    before: vendedor.amount,
                    after: roundMoney(vendedor.amount + ingresoVendedor),
                },
            ];

            setPersonas((prev) =>
                prev.map((p) => {
                    if (p.id === compradorId) {
                        return { ...p, amount: roundMoney(p.amount - precio) };
                    }

                    if (p.id === vendedorId) {
                        return {
                            ...p,
                            amount: roundMoney(p.amount + ingresoVendedor),
                        };
                    }

                    return p;
                }),
            );

            setMovimientos((prev) => [
                {
                    id: `mov_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
                    type: "compra",
                    buyerName: comprador.name,
                    sellerName: vendedor.name,
                    price: precio,
                    taxRate,
                    sellerGain: ingresoVendedor,
                    changes,
                },
                ...prev,
            ]);

            setCuenta("");
            clearSelection();
            return;
        }

        const targetId = compradorId ?? vendedorId;
        const target = personas.find((p) => p.id === targetId);
        if (!target) return;

        const changes = [
            {
                id: target.id,
                name: target.name,
                before: target.amount,
                after: roundMoney(target.amount + valor),
            },
        ];

        setPersonas((prev) =>
            prev.map((p) =>
                p.id === targetId
                    ? { ...p, amount: roundMoney(p.amount + valor) }
                    : p,
            ),
        );

        setMovimientos((prev) => [
            {
                id: `mov_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
                type: "ajuste",
                name: target.name,
                amount: valor,
                changes,
            },
            ...prev,
        ]);

        setCuenta("");
        clearSelection();
    };

    const onResetear = () => {
        setPersonas((prev) =>
            prev.map((p) => ({ ...p, amount: DINERO_INICIAL })),
        );
        setCuenta("");
        clearSelection();
        setTaxIndex(0);
        setMovimientos([]);
        setResetOpen(false);
    };

    const openEditar = () => {
        setEditPersonas(personas.map((p) => ({ ...p })));
        setEditOpen(true);
    };

    const closeEditar = () => {
        setEditOpen(false);
        setEditPersonas([]);
    };

    const saveEditar = () => {
        const cleaned = editPersonas.map((p, idx) => ({
            ...p,
            name: (p.name ?? "").trim() || DEFAULT_NAMES[idx] || "Jugador",
        }));

        const allowed = new Set(cleaned.map((p) => p.id));

        setPersonas(cleaned);
        setCompradorId((prev) => (allowed.has(prev) ? prev : null));
        setVendedorId((prev) => (allowed.has(prev) ? prev : null));
        setLastSelectedTap(null);
        closeEditar();
    };

    const setPlayerCount = (count) => {
        setEditPersonas((prev) => createPersonas(count, prev));
    };

    const updateJugadorName = (id, name) => {
        setEditPersonas((prev) =>
            prev.map((p) => (p.id === id ? { ...p, name } : p)),
        );
    };

    const actionLabel = compradorId && vendedorId ? "Comprar" : "Aplicar";

    const keypad = [
        { label: "7", onClick: () => onDigit("7"), xs: 3, variant: "outlined" },
        { label: "8", onClick: () => onDigit("8"), xs: 3, variant: "outlined" },
        { label: "9", onClick: () => onDigit("9"), xs: 3, variant: "outlined" },
        {
            label: "+",
            onClick: () => onOp("+"),
            xs: 3,
            variant: "contained",
            icon: <AddIcon fontSize="inherit" />,
        },

        { label: "4", onClick: () => onDigit("4"), xs: 3, variant: "outlined" },
        { label: "5", onClick: () => onDigit("5"), xs: 3, variant: "outlined" },
        { label: "6", onClick: () => onDigit("6"), xs: 3, variant: "outlined" },
        {
            label: "-",
            onClick: () => onOp("-"),
            xs: 3,
            variant: "contained",
            icon: <RemoveIcon fontSize="inherit" />,
        },

        { label: "1", onClick: () => onDigit("1"), xs: 3, variant: "outlined" },
        { label: "2", onClick: () => onDigit("2"), xs: 3, variant: "outlined" },
        { label: "3", onClick: () => onDigit("3"), xs: 3, variant: "outlined" },
        {
            label: "retroceso",
            onClick: onBackspace,
            xs: 3,
            variant: "contained",
            icon: <BackspaceIcon fontSize="inherit" />,
        },

        { label: "0", onClick: () => onDigit("0"), xs: 3, variant: "outlined" },
        { label: ",", onClick: onComma, xs: 3, variant: "outlined" },
        { label: actionLabel, onClick: onAplicar, xs: 6, variant: "contained" },
    ];

    return (
        <Container
            maxWidth={false}
            disableGutters
            sx={{ height: "100vh", position: "relative" }}
        >
            <Stack
                direction="column"
                sx={{
                    height: "100%",
                    width: "100%",
                    "@media (orientation: landscape) and (min-width: 900px)": {
                        flexDirection: "row",
                    },
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        flex: "0 0 auto",
                        "@media (orientation: landscape) and (min-width: 900px)":
                            {
                                flex: "0 0 42%",
                                height: "100%",
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                            },
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                    >
                        <Typography variant="body1" noWrap>
                            Propiedades
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{ opacity: 0.7 }}
                            noWrap
                        >
                            Inicio: {DINERO_INICIAL}k€
                        </Typography>

                        <Box sx={{ flex: 1 }} />

                        <IconButton
                            onClick={() => setHistorialOpen(true)}
                            size="small"
                            aria-label="Historial"
                        >
                            <Badge
                                badgeContent={movimientos.length}
                                color="primary"
                            >
                                <HistoryIcon />
                            </Badge>
                        </IconButton>

                        <IconButton onClick={openEditar} size="small">
                            <GroupAddIcon />
                        </IconButton>

                        <IconButton
                            onClick={() => setResetOpen(true)}
                            size="small"
                        >
                            <RestartAltIcon />
                        </IconButton>
                    </Stack>

                    <Paper
                        variant="outlined"
                        sx={{
                            maxHeight: "35vh",
                            overflow: "auto",
                            p: 1,
                            "@media (orientation: landscape) and (min-width: 900px)":
                                {
                                    flex: 1,
                                    minHeight: 0,
                                    maxHeight: "none",
                                },
                        }}
                    >
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {personas.map((p) => {
                                const role =
                                    p.id === compradorId
                                        ? "compra"
                                        : p.id === vendedorId
                                          ? "vende"
                                          : "";

                                const isComprador = role === "compra";
                                const isVendedor = role === "vende";
                                const isNeg = p.amount < 0;

                                return (
                                    <Button
                                        key={p.id}
                                        onClick={() => onJugadorClick(p.id)}
                                        variant={
                                            role ? "contained" : "outlined"
                                        }
                                        color={
                                            isComprador
                                                ? "warning"
                                                : isVendedor
                                                  ? "success"
                                                  : "primary"
                                        }
                                        sx={{
                                            textTransform: "none",
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: 2,
                                            minWidth: 0,
                                            borderColor: isNeg
                                                ? "error.main"
                                                : undefined,
                                            ...(!role && isNeg
                                                ? { color: "error.main" }
                                                : {}),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                lineHeight: 1.05,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: 18,
                                                    fontWeight: 700,
                                                }}
                                                noWrap
                                            >
                                                {p.name}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: 16,
                                                    fontWeight: 700,
                                                    opacity: 0.9,
                                                }}
                                                noWrap
                                            >
                                                {formatMoney(p.amount)}k€
                                            </Typography>

                                            {role && (
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        opacity: 0.95,
                                                        mt: 0.25,
                                                    }}
                                                    noWrap
                                                >
                                                    {role}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Button>
                                );
                            })}
                        </Box>
                    </Paper>
                </Box>

                <Box
                    sx={{
                        borderTop: 1,
                        borderColor: "divider",
                        "@media (orientation: landscape) and (min-width: 900px)":
                            {
                                borderTop: 0,
                                borderLeft: 1,
                            },
                    }}
                />

                <Box
                    sx={{
                        p: 2,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        "@media (orientation: landscape) and (min-width: 900px)":
                            {
                                height: "100%",
                                minWidth: 0,
                            },
                    }}
                >
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    setTaxIndex(
                                        (prev) => (prev + 1) % IMPUESTOS.length,
                                    )
                                }
                                color={taxRate !== 50 ? "error" : undefined}
                                sx={{
                                    fontWeight: 800,
                                }}
                            >
                                {taxRate}%
                            </Button>

                            <PisosMoneyTokens amount={valor} />
                        </Stack>

                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            Precio / ajuste en k€
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{ wordBreak: "break-word", minHeight: 32 }}
                        >
                            {cuenta || "—"}
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Stack
                            direction="row"
                            alignItems="baseline"
                            spacing={1}
                        >
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                Total
                            </Typography>
                            <Typography variant="h4">
                                {formatMoney(valor)}k€
                            </Typography>
                        </Stack>

                        {compradorId && vendedorId && (
                            <Typography
                                variant="body2"
                                sx={{ opacity: 0.7, mt: 1 }}
                            >
                                El vendedor recibe{" "}
                                {formatMoney(valor * ((100 - taxRate) / 100))}k€
                            </Typography>
                        )}
                    </Paper>

                    <Paper
                        variant="outlined"
                        sx={{ p: 2, flex: 1, minHeight: 0, display: "flex" }}
                    >
                        <Grid container spacing={1} sx={{ width: "100%" }}>
                            {keypad.map((k, idx) => (
                                <Grid size={k.xs} key={`${k.label}_${idx}`}>
                                    <Button
                                        fullWidth
                                        size="large"
                                        variant={k.variant}
                                        sx={{
                                            minHeight: 64,
                                            height: "100%",
                                            fontSize: k.icon
                                                ? "clamp(28px, 6vw, 70px)"
                                                : "clamp(14px, 5vw, 50px)",
                                        }}
                                        onClick={k.onClick}
                                        disabled={
                                            k.label === actionLabel &&
                                            (!valor ||
                                                (!compradorId && !vendedorId))
                                        }
                                    >
                                        {k.icon ? k.icon : k.label}
                                    </Button>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Box>

                <Divider
                    sx={{
                        "@media (orientation: landscape) and (min-width: 900px)":
                            {
                                display: "none",
                            },
                    }}
                />
            </Stack>

            <Dialog
                open={editOpen}
                onClose={closeEditar}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{ display: "flex", alignItems: "center", pr: 1 }}
                >
                    <Typography variant="h6" sx={{ flex: 1 }}>
                        Elegir jugadores
                    </Typography>

                    <IconButton
                        onClick={closeEditar}
                        aria-label="Cerrar"
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {PLAYER_COUNTS.map((count) => (
                                <Button
                                    key={count}
                                    variant={
                                        editPersonas.length === count
                                            ? "contained"
                                            : "outlined"
                                    }
                                    onClick={() => setPlayerCount(count)}
                                >
                                    {count}
                                </Button>
                            ))}
                        </Stack>

                        <Divider />

                        <Stack spacing={1.25}>
                            {editPersonas.map((p) => (
                                <TextField
                                    key={p.id}
                                    fullWidth
                                    size="small"
                                    value={p.name}
                                    onChange={(e) =>
                                        updateJugadorName(p.id, e.target.value)
                                    }
                                />
                            ))}
                        </Stack>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeEditar}>Cancelar</Button>
                    <Button variant="contained" onClick={saveEditar}>
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={historialOpen}
                onClose={() => setHistorialOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                    <HistoryIcon />
                    Historial
                    <Box sx={{ flex: 1 }} />
                    <IconButton
                        onClick={() => setHistorialOpen(false)}
                        aria-label="Cerrar"
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {movimientos.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            No hay movimientos desde el último reset.
                        </Typography>
                    ) : (
                        <Paper variant="outlined">
                            <List dense>
                                {movimientos.map((m) => {
                                    let primary = null;
                                    let secondary = null;

                                    if (m.type === "compra") {
                                        primary = (
                                            <span>
                                                <strong>{m.buyerName}</strong>{" "}
                                                compra a{" "}
                                                <strong>{m.sellerName}</strong>{" "}
                                                por{" "}
                                                <strong>
                                                    {formatMoney(m.price)}k€
                                                </strong>
                                            </span>
                                        );

                                        secondary = `Impuesto ${m.taxRate}% · ${m.sellerName} recibe ${formatMoney(
                                            m.sellerGain,
                                        )}k€`;
                                    }

                                    if (m.type === "ajuste") {
                                        primary = (
                                            <span>
                                                <strong>{m.name}</strong>{" "}
                                                {m.amount >= 0
                                                    ? "recibe"
                                                    : "pierde"}{" "}
                                                <strong>
                                                    {formatMoney(
                                                        Math.abs(m.amount),
                                                    )}
                                                    k€
                                                </strong>
                                            </span>
                                        );

                                        const c = m.changes[0];
                                        secondary = `${formatMoney(c.before)}k€ → ${formatMoney(
                                            c.after,
                                        )}k€`;
                                    }

                                    return (
                                        <ListItem key={m.id}>
                                            <ListItemText
                                                primary={primary}
                                                secondary={secondary}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Paper>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setHistorialOpen(false)}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={resetOpen} onClose={() => setResetOpen(false)}>
                <DialogTitle>Resetear partida</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        ¿Seguro que quieres resetear el dinero, impuestos e
                        historial?
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setResetOpen(false)}>
                        Cancelar
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={onResetear}
                    >
                        Resetear
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={toast.open}
                autoHideDuration={2500}
                onClose={closeToast}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={closeToast}
                    severity={toast.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default PisosScreen;
